import { Injectable } from '@angular/core';
import { ServicoCriptografiaLocal } from './servico-criptografia-local';

export type SituacaoRegistroPendente = 'PENDENTE' | 'SINCRONIZADO' | 'FALHA';

export interface RegistroPontoPendente {
  offlineId: string;
  criadoEm: string;
  atualizadoEm: string;
  imagemFacialBase64?: string;
  imagemFacialMimeType?: string;
  descritorFacial?: number[];
  funcionarioId?: string;
  funcionarioNome?: string;
  funcionarioMatricula?: string;
  situacao: SituacaoRegistroPendente;
  tentativas: number;
  ultimaMensagem?: string;
  nsr?: number;
  registroId?: string;
  dataHoraServidor?: string;
}

export interface ResumoRegistroPontoPendente {
  offlineId: string;
  criadoEm: string;
  atualizadoEm: string;
  situacao: SituacaoRegistroPendente;
  tentativas: number;
  funcionarioNome?: string;
  funcionarioMatricula?: string;
  ultimaMensagem?: string;
  nsr?: number;
  dataHoraServidor?: string;
  possuiBiometriaPendente: boolean;
}

export interface EstadoFilaPontoLocal {
  registrosPendentes: number;
  resumos: ResumoRegistroPontoPendente[];
  totalPendentes: number;
  totalFalhas: number;
  totalSincronizados: number;
}

interface RegistroPontoPendenteArmazenado extends Partial<RegistroPontoPendente> {
  offlineId: string;
  dadosCriptografados?: string;
}

@Injectable({ providedIn: 'root' })
export class ServicoFilaPontoLocal {
  private readonly nomeBanco = 'pontojwm-terminal';
  private readonly versaoBanco = 3;
  private readonly nomeArmazenamento = 'registros-ponto-pendentes';
  private readonly contextoCriptografia = 'fila-ponto-offline';
  private banco?: Promise<IDBDatabase>;

  constructor(private readonly servicoCriptografiaLocal: ServicoCriptografiaLocal) {}

  async criarPendente(
    tokenTerminal: string,
    dados: Omit<RegistroPontoPendente, 'offlineId' | 'criadoEm' | 'atualizadoEm' | 'situacao' | 'tentativas'>
  ) {
    const agora = new Date().toISOString();
    const registro: RegistroPontoPendente = {
      ...dados,
      offlineId: crypto.randomUUID(),
      criadoEm: agora,
      atualizadoEm: agora,
      situacao: 'PENDENTE',
      tentativas: 0
    };
    await this.salvar(tokenTerminal, registro);
    return registro;
  }

  async listarPendentes(tokenTerminal: string) {
    const registros = await this.listarTodos(tokenTerminal);
    return registros
      .filter(registro => registro.situacao === 'PENDENTE' || registro.situacao === 'FALHA')
      .sort((a, b) => a.criadoEm.localeCompare(b.criadoEm));
  }

  async possuiRegistroRecenteDoFuncionario(
    tokenTerminal: string,
    funcionarioId: string,
    intervaloMs: number
  ) {
    if (!funcionarioId) {
      return false;
    }

    const limite = Date.now() - intervaloMs;
    const registros = await this.listarTodos(tokenTerminal);
    return registros.some(registro =>
      registro.funcionarioId === funcionarioId
      && registro.situacao !== 'FALHA'
      && new Date(registro.criadoEm).getTime() >= limite
    );
  }

  async contarPendentesSincronizaveis(tokenTerminal: string) {
    const registros = await this.listarTodos(tokenTerminal);
    return registros.filter(registro =>
      registro.situacao === 'PENDENTE'
      && !!registro.imagemFacialBase64
      && !!registro.imagemFacialMimeType
      && !!registro.descritorFacial?.length
    ).length;
  }

  async contarPendentes(tokenTerminal: string) {
    return (await this.listarPendentes(tokenTerminal)).length;
  }

  async consultarEstado(tokenTerminal: string): Promise<EstadoFilaPontoLocal> {
    const resumos = await this.listarResumo(tokenTerminal);
    return {
      registrosPendentes: resumos.filter(item => item.situacao === 'PENDENTE' || item.situacao === 'FALHA').length,
      resumos,
      totalPendentes: resumos.filter(item => item.situacao === 'PENDENTE').length,
      totalFalhas: resumos.filter(item => item.situacao === 'FALHA').length,
      totalSincronizados: resumos.filter(item => item.situacao === 'SINCRONIZADO').length
    };
  }

  montarResumo(registro: RegistroPontoPendente): ResumoRegistroPontoPendente {
    return {
      offlineId: registro.offlineId,
      criadoEm: registro.criadoEm,
      atualizadoEm: registro.atualizadoEm,
      situacao: registro.situacao,
      tentativas: registro.tentativas,
      funcionarioNome: registro.funcionarioNome,
      funcionarioMatricula: registro.funcionarioMatricula,
      ultimaMensagem: registro.ultimaMensagem,
      nsr: registro.nsr,
      dataHoraServidor: registro.dataHoraServidor,
      possuiBiometriaPendente: !!registro.imagemFacialBase64 || !!registro.descritorFacial?.length
    };
  }

  async listarResumo(tokenTerminal: string): Promise<ResumoRegistroPontoPendente[]> {
    const registros = await this.listarTodos(tokenTerminal);
    return registros
      .sort((a, b) => b.criadoEm.localeCompare(a.criadoEm))
      .map(registro => this.montarResumo(registro));
  }

  async marcarSincronizado(tokenTerminal: string, offlineId: string, nsr?: number, registroId?: string, dataHoraServidor?: string) {
    const registro = await this.buscar(tokenTerminal, offlineId);
    if (!registro) {
      return;
    }
    registro.situacao = 'SINCRONIZADO';
    registro.imagemFacialBase64 = undefined;
    registro.imagemFacialMimeType = undefined;
    registro.descritorFacial = undefined;
    registro.nsr = nsr;
    registro.registroId = registroId;
    registro.dataHoraServidor = dataHoraServidor;
    registro.atualizadoEm = new Date().toISOString();
    registro.ultimaMensagem = undefined;
    await this.salvar(tokenTerminal, registro);
  }

  async marcarFalha(tokenTerminal: string, offlineId: string, mensagem: string) {
    const registro = await this.buscar(tokenTerminal, offlineId);
    if (!registro) {
      return;
    }
    registro.situacao = 'FALHA';
    registro.tentativas += 1;
    registro.ultimaMensagem = mensagem;
    registro.atualizadoEm = new Date().toISOString();
    await this.salvar(tokenTerminal, registro);
  }

  private async listarTodos(tokenTerminal: string) {
    const banco = await this.abrirBanco();
    const armazenados = await new Promise<RegistroPontoPendenteArmazenado[]>((resolve, reject) => {
      const transacao = banco.transaction(this.nomeArmazenamento, 'readonly');
      const requisicao = transacao.objectStore(this.nomeArmazenamento).getAll();
      requisicao.onsuccess = () => resolve(requisicao.result as RegistroPontoPendenteArmazenado[]);
      requisicao.onerror = () => reject(requisicao.error);
    });

    const registros: RegistroPontoPendente[] = [];
    for (const armazenado of armazenados) {
      try {
        const registro = await this.abrirRegistro(tokenTerminal, armazenado);
        if (registro?.offlineId) {
          registros.push(registro);
        }
      } catch {
        continue;
      }
    }
    return registros;
  }

  private async buscar(tokenTerminal: string, offlineId: string) {
    const banco = await this.abrirBanco();
    return new Promise<RegistroPontoPendenteArmazenado | undefined>((resolve, reject) => {
      const transacao = banco.transaction(this.nomeArmazenamento, 'readonly');
      const requisicao = transacao.objectStore(this.nomeArmazenamento).get(offlineId);
      requisicao.onsuccess = () => resolve(requisicao.result as RegistroPontoPendenteArmazenado | undefined);
      requisicao.onerror = () => reject(requisicao.error);
    }).then(registro => registro ? this.abrirRegistro(tokenTerminal, registro) : undefined);
  }

  private async salvar(tokenTerminal: string, registro: RegistroPontoPendente) {
    const banco = await this.abrirBanco();
    const dadosCriptografados = await this.servicoCriptografiaLocal.criptografarJson(
      registro,
      tokenTerminal,
      this.contextoCriptografia
    );
    const armazenado: RegistroPontoPendenteArmazenado = {
      offlineId: registro.offlineId,
      dadosCriptografados
    };
    return new Promise<void>((resolve, reject) => {
      const transacao = banco.transaction(this.nomeArmazenamento, 'readwrite');
      transacao.objectStore(this.nomeArmazenamento).put(armazenado);
      transacao.oncomplete = () => resolve();
      transacao.onerror = () => reject(transacao.error);
    });
  }

  private async abrirRegistro(tokenTerminal: string, registro: RegistroPontoPendenteArmazenado): Promise<RegistroPontoPendente> {
    if (!registro.dadosCriptografados) {
      return registro as RegistroPontoPendente;
    }
    return this.servicoCriptografiaLocal.descriptografarJson<RegistroPontoPendente>(
      registro.dadosCriptografados,
      tokenTerminal,
      this.contextoCriptografia
    );
  }

  private abrirBanco() {
    this.banco ??= new Promise<IDBDatabase>((resolve, reject) => {
      const requisicao = indexedDB.open(this.nomeBanco, this.versaoBanco);
      requisicao.onupgradeneeded = () => {
        const banco = requisicao.result;
        if (!banco.objectStoreNames.contains(this.nomeArmazenamento)) {
          banco.createObjectStore(this.nomeArmazenamento, { keyPath: 'offlineId' });
        }
        if (!banco.objectStoreNames.contains('cache-biometrico-funcionarios')) {
          banco.createObjectStore('cache-biometrico-funcionarios', { keyPath: 'chaveCache' });
        }
      };
      requisicao.onsuccess = () => resolve(requisicao.result);
      requisicao.onerror = () => reject(requisicao.error);
    });
    return this.banco;
  }
}
